export interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  specification: string;
  purity: string;
  price: number;
  image: string;
  researchCategories: string[];
  mechanism?: string;
  researchContext?: string;
}

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
}

export const productCategories: ProductCategory[] = [
  {
    id: 'weight-management',
    name: 'Weight Management',
    slug: 'weight-management',
    description: 'Research compounds for metabolic and weight-related studies',
    icon: 'ri-scales-3-line',
    color: 'from-teal-500 to-teal-600',
  },
  {
    id: 'recovery-repair',
    name: 'Recovery & Repair',
    slug: 'recovery-repair',
    description: 'Peptides for tissue repair and recovery research',
    icon: 'ri-heart-pulse-line',
    color: 'from-green-500 to-green-600',
  },
  {
    id: 'longevity-cellular',
    name: 'Longevity & Cellular',
    slug: 'longevity-cellular',
    description: 'Compounds for cellular health and longevity studies',
    icon: 'ri-dna-line',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    id: 'growth-hormone',
    name: 'Growth Hormone',
    slug: 'growth-hormone',
    description: 'Growth hormone secretagogues and related peptides',
    icon: 'ri-line-chart-line',
    color: 'from-cyan-500 to-cyan-600',
  },
  {
    id: 'cognitive-neuro',
    name: 'Cognitive & Neuro',
    slug: 'cognitive-neuro',
    description: 'Neuropeptides for cognitive research',
    icon: 'ri-brain-line',
    color: 'from-teal-600 to-teal-700',
  },
  {
    id: 'metabolic',
    name: 'Metabolic',
    slug: 'metabolic',
    description: 'Metabolic regulation and energy homeostasis research',
    icon: 'ri-flashlight-line',
    color: 'from-green-600 to-green-700',
  },
  {
    id: 'hormone-peptide',
    name: 'Hormone & Peptide',
    slug: 'hormone-peptide',
    description: 'Hormone-related peptides for research',
    icon: 'ri-medicine-bottle-line',
    color: 'from-emerald-600 to-emerald-700',
  },
];

// Clean product image: clear glass vial with simple white label showing only product name on dark neutral background
const VIAL_IMAGE = 'https://public.readdy.ai/ai/img_res/ed838d5b4ac477222e53606c45d4a6cc.jpg';

export const allProducts: Product[] = [
  {
    id: '5-amino-1mq-5mg',
    name: '5-amino-1MQ — 5mg vial',
    slug: '5-amino-1mq-5mg',
    category: 'metabolic',
    specification: '5mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Metabolic Research', 'Weight Management Research'],
    mechanism: '5-amino-1MQ is a small molecule that inhibits nicotinamide N-methyltransferase (NNMT).',
    researchContext: 'Preclinical studies have investigated 5-amino-1MQ for its potential effects on cellular metabolism and energy expenditure in laboratory models.',
  },
  {
    id: '5-amino-1mq-50mg',
    name: '5-amino-1MQ — 50mg vial',
    slug: '5-amino-1mq-50mg',
    category: 'metabolic',
    specification: '50mg vial',
    purity: '99%',
    price: 412.8,
    image: VIAL_IMAGE,
    researchCategories: ['Metabolic Research', 'Weight Management Research'],
    mechanism: '5-amino-1MQ is a small molecule that inhibits nicotinamide N-methyltransferase (NNMT).',
    researchContext: 'Preclinical studies have investigated 5-amino-1MQ for its potential effects on cellular metabolism and energy expenditure in laboratory models.',
  },
  {
    id: 'aod-9604-2mg',
    name: 'AOD-9604 — 2mg vial',
    slug: 'aod-9604-2mg',
    category: 'weight-management',
    specification: '2mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'AOD-9604 is a modified fragment of human growth hormone (hGH).',
    researchContext: 'Research suggests AOD-9604 may influence lipolytic mechanisms in preclinical models.',
  },
  {
    id: 'aod-9604-5mg',
    name: 'AOD-9604 — 5mg vial',
    slug: 'aod-9604-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 388.8,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'AOD-9604 is a modified fragment of human growth hormone (hGH).',
    researchContext: 'Research suggests AOD-9604 may influence lipolytic mechanisms in preclinical models.',
  },
  {
    id: 'ara-290-10mg',
    name: 'ARA-290 — 10mg vial',
    slug: 'ara-290-10mg',
    category: 'recovery-repair',
    specification: '10mg vial',
    purity: '99%',
    price: 278.4,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Neuropeptide Research'],
    mechanism: 'ARA-290 is a synthetic peptide derived from erythropoietin (EPO).',
    researchContext: 'Preclinical research has investigated ARA-290 for its potential tissue-protective properties in laboratory settings.',
  },
  {
    id: 'adipotide-2mg',
    name: 'Adipotide — 2mg vial',
    slug: 'adipotide-2mg',
    category: 'weight-management',
    specification: '2mg vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Adipotide is a peptidomimetic targeting adipose tissue vasculature.',
    researchContext: 'Preclinical studies have investigated adipotide for its effects on adipose tissue in laboratory models.',
  },
  {
    id: 'adipotide-5mg',
    name: 'Adipotide — 5mg vial',
    slug: 'adipotide-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 667.2,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Adipotide is a peptidomimetic targeting adipose tissue vasculature.',
    researchContext: 'Preclinical studies have investigated adipotide for its effects on adipose tissue in laboratory models.',
  },
  {
    id: 'bpc-tb-10mg',
    name: 'BPC + TB — 10mg/10mg vial',
    slug: 'bpc-tb-10mg',
    category: 'recovery-repair',
    specification: 'BPC 10mg + TB 10mg vial',
    purity: '99%',
    price: 576,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'Combination of BPC-157 and TB-500 peptides for synergistic research applications.',
    researchContext: 'Combined formulation studied for potential complementary effects on tissue repair mechanisms in laboratory models.',
  },
  {
    id: 'bpc-tb-20mg',
    name: 'BPC + TB — 20mg/20mg vial',
    slug: 'bpc-tb-20mg',
    category: 'recovery-repair',
    specification: 'BPC 20mg + TB 20mg vial',
    purity: '99%',
    price: 1092,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'Combination of BPC-157 and TB-500 peptides for synergistic research applications.',
    researchContext: 'Combined formulation studied for potential complementary effects on tissue repair mechanisms in laboratory models.',
  },
  {
    id: 'bpc-157-10mg',
    name: 'BPC-157 — 10mg vial',
    slug: 'bpc-157-10mg',
    category: 'recovery-repair',
    specification: '10mg vial',
    purity: '99%',
    price: 268.8,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'BPC-157 is a pentadecapeptide derived from body protection compound found in gastric juice.',
    researchContext: 'In preclinical studies, BPC-157 has been investigated for its potential effects on tissue repair mechanisms and angiogenesis in cell and animal models.',
  },
  {
    id: 'bpc-157-5mg',
    name: 'BPC-157 — 5mg vial',
    slug: 'bpc-157-5mg',
    category: 'recovery-repair',
    specification: '5mg vial',
    purity: '99%',
    price: 153.6,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'BPC-157 is a pentadecapeptide derived from body protection compound found in gastric juice.',
    researchContext: 'In preclinical studies, BPC-157 has been investigated for its potential effects on tissue repair mechanisms and angiogenesis in cell and animal models.',
  },
  {
    id: 'bac-water-10ml',
    name: 'Bac Water — 10ml vial',
    slug: 'bac-water-10ml',
    category: 'hormone-peptide',
    specification: '10ml vial',
    purity: '99%',
    price: 45.6,
    image: VIAL_IMAGE,
    researchCategories: ['Laboratory Supplies'],
    mechanism: 'Bacteriostatic water for reconstitution of lyophilized peptides.',
    researchContext: 'Sterile water containing 0.9% benzyl alcohol used for peptide reconstitution in research settings.',
  },
  {
    id: 'cjc-ipamorelin-10mg',
    name: 'CJC + Ipamorelin — 10mg/10mg vial',
    slug: 'cjc-ipamorelin-10mg',
    category: 'growth-hormone',
    specification: 'CJC 10mg + Ipa 10mg vial',
    purity: '99%',
    price: 552,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'Combination of CJC-1295 and Ipamorelin for synergistic growth hormone research.',
    researchContext: 'Combined formulation studied for potential complementary effects on growth hormone pathways in laboratory models.',
  },
  {
    id: 'cjc-1295-no-dac-10mg',
    name: 'CJC-1295 (No DAC) — 10mg vial',
    slug: 'cjc-1295-no-dac-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 619.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'CJC-1295 without DAC is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests CJC-1295 may influence growth hormone secretion patterns in preclinical models.',
  },
  {
    id: 'cjc-1295-no-dac-2mg',
    name: 'CJC-1295 (No DAC) — 2mg vial',
    slug: 'cjc-1295-no-dac-2mg',
    category: 'growth-hormone',
    specification: '2mg vial',
    purity: '99%',
    price: 153.6,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'CJC-1295 without DAC is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests CJC-1295 may influence growth hormone secretion patterns in preclinical models.',
  },
  {
    id: 'cjc-1295-no-dac-5mg',
    name: 'CJC-1295 (No DAC) — 5mg vial',
    slug: 'cjc-1295-no-dac-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 331.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'CJC-1295 without DAC is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests CJC-1295 may influence growth hormone secretion patterns in preclinical models.',
  },
  {
    id: 'cjc-1295-dac-2mg',
    name: 'CJC-1295 (DAC) — 2mg vial',
    slug: 'cjc-1295-dac-2mg',
    category: 'growth-hormone',
    specification: '2mg vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'CJC-1295 with DAC is a growth hormone-releasing hormone (GHRH) analog with Drug Affinity Complex.',
    researchContext: 'Research suggests CJC-1295 with DAC may influence growth hormone secretion patterns in preclinical models.',
  },
  {
    id: 'cjc-1295-dac-5mg',
    name: 'CJC-1295 (DAC) — 5mg vial',
    slug: 'cjc-1295-dac-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 667.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Growth Hormone Research'],
    mechanism: 'CJC-1295 with DAC is a growth hormone-releasing hormone (GHRH) analog with Drug Affinity Complex.',
    researchContext: 'Research suggests CJC-1295 with DAC may influence growth hormone secretion patterns in preclinical models.',
  },
  {
    id: 'cagrilintide-10mg',
    name: 'Cagrilintide — 10mg vial',
    slug: 'cagrilintide-10mg',
    category: 'weight-management',
    specification: '10mg vial',
    purity: '99%',
    price: 756,
    image: VIAL_IMAGE,
    researchCategories: ['Metabolic Research', 'Weight Management Research'],
    mechanism: 'Cagrilintide is a long-acting amylin analog.',
    researchContext: 'Research suggests cagrilintide may influence appetite regulation and gastric emptying in preclinical models.',
  },
  {
    id: 'cagrilintide-5mg',
    name: 'Cagrilintide — 5mg vial',
    slug: 'cagrilintide-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 384,
    image: VIAL_IMAGE,
    researchCategories: ['Metabolic Research', 'Weight Management Research'],
    mechanism: 'Cagrilintide is a long-acting amylin analog.',
    researchContext: 'Research suggests cagrilintide may influence appetite regulation and gastric emptying in preclinical models.',
  },
  {
    id: 'cerebrolysin',
    name: 'Cerebrolysin — 10ml vial',
    slug: 'cerebrolysin',
    category: 'cognitive-neuro',
    specification: '10ml vial',
    purity: '99%',
    price: 134.4,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'Cerebrolysin is a peptide preparation derived from porcine brain proteins.',
    researchContext: 'Preclinical research has investigated cerebrolysin for its potential neuroprotective properties in laboratory models.',
  },
  {
    id: 'dsip-10mg',
    name: 'DSIP — 10mg vial',
    slug: 'dsip-10mg',
    category: 'cognitive-neuro',
    specification: '10mg vial',
    purity: '99%',
    price: 331.2,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'DSIP (Delta Sleep-Inducing Peptide) is a neuropeptide.',
    researchContext: 'Research suggests DSIP may influence sleep patterns and stress response in preclinical models.',
  },
  {
    id: 'dsip-5mg',
    name: 'DSIP — 5mg vial',
    slug: 'dsip-5mg',
    category: 'cognitive-neuro',
    specification: '5mg vial',
    purity: '99%',
    price: 172.8,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'DSIP (Delta Sleep-Inducing Peptide) is a neuropeptide.',
    researchContext: 'Research suggests DSIP may influence sleep patterns and stress response in preclinical models.',
  },
  {
    id: 'dermorphin-5mg',
    name: 'Dermorphin — 5mg vial',
    slug: 'dermorphin-5mg',
    category: 'cognitive-neuro',
    specification: '5mg vial',
    purity: '99%',
    price: 144,
    image: VIAL_IMAGE,
    researchCategories: ['Neuropeptide Research'],
    mechanism: 'Dermorphin is an opioid peptide.',
    researchContext: 'Preclinical research has investigated dermorphin for its opioid receptor binding properties in laboratory settings.',
  },
  {
    id: 'epithalon-10mg',
    name: 'Epithalon — 10mg vial',
    slug: 'epithalon-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 160,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Anti-Aging Research'],
    mechanism: 'Epithalon is a synthetic tetrapeptide that may influence telomerase activity.',
    researchContext: 'Preclinical research has investigated Epithalon for its potential effects on cellular aging mechanisms in laboratory models.',
  },
  {
    id: 'epithalon-50mg',
    name: 'Epithalon — 50mg vial',
    slug: 'epithalon-50mg',
    category: 'longevity-cellular',
    specification: '50mg vial',
    purity: '99%',
    price: 695,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Anti-Aging Research'],
    mechanism: 'Epithalon is a synthetic tetrapeptide that may influence telomerase activity.',
    researchContext: 'Preclinical research has investigated Epithalon for its potential effects on cellular aging mechanisms in laboratory models.',
  },
  {
    id: 'foxo4-10mg',
    name: 'FOXO4 — 10mg vial',
    slug: 'foxo4-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 1500,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Anti-Aging Research'],
    mechanism: 'FOXO4-DRI is a peptide that interferes with FOXO4-p53 interaction.',
    researchContext: 'Preclinical research has investigated FOXO4-DRI for its potential effects on senescent cell clearance in laboratory models.',
  },
  {
    id: 'ghk-cu-100mg',
    name: 'GHK-Cu — 100mg vial',
    slug: 'ghk-cu-100mg',
    category: 'longevity-cellular',
    specification: '100mg vial',
    purity: '99%',
    price: 235,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Recovery & Repair Research'],
    mechanism: 'GHK-Cu is a copper peptide complex.',
    researchContext: 'Research suggests GHK-Cu may influence tissue remodeling and wound healing mechanisms in preclinical models.',
  },
  {
    id: 'ghk-cu-50mg',
    name: 'GHK-Cu — 50mg vial',
    slug: 'ghk-cu-50mg',
    category: 'longevity-cellular',
    specification: '50mg vial',
    purity: '99%',
    price: 130,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Recovery & Repair Research'],
    mechanism: 'GHK-Cu is a copper peptide complex.',
    researchContext: 'Research suggests GHK-Cu may influence tissue remodeling and wound healing mechanisms in preclinical models.',
  },
  {
    id: 'ghrp-2-10mg',
    name: 'GHRP-2 — 10mg vial',
    slug: 'ghrp-2-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 187.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'GHRP-2 is a growth hormone-releasing peptide.',
    researchContext: 'Research suggests GHRP-2 may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'ghrp-2-15mg',
    name: 'GHRP-2 — 15mg vial',
    slug: 'ghrp-2-15mg',
    category: 'growth-hormone',
    specification: '15mg vial',
    purity: '99%',
    price: 268.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'GHRP-2 is a growth hormone-releasing peptide.',
    researchContext: 'Research suggests GHRP-2 may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'ghrp-2-5mg',
    name: 'GHRP-2 — 5mg vial',
    slug: 'ghrp-2-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 100.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'GHRP-2 is a growth hormone-releasing peptide.',
    researchContext: 'Research suggests GHRP-2 may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'ghrp-6-10mg',
    name: 'GHRP-6 — 10mg vial',
    slug: 'ghrp-6-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 187.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'GHRP-6 is a growth hormone-releasing peptide.',
    researchContext: 'Research suggests GHRP-6 may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'ghrp-6-5mg',
    name: 'GHRP-6 — 5mg vial',
    slug: 'ghrp-6-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 100.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'GHRP-6 is a growth hormone-releasing peptide.',
    researchContext: 'Research suggests GHRP-6 may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'glutathione-1500mg',
    name: 'Glutathione — 1500mg vial',
    slug: 'glutathione-1500mg',
    category: 'longevity-cellular',
    specification: '1500mg vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Antioxidant Research'],
    mechanism: 'Glutathione is a tripeptide antioxidant.',
    researchContext: 'Research suggests glutathione may influence oxidative stress and cellular detoxification in preclinical models.',
  },
  {
    id: 'glutathione-600mg',
    name: 'Glutathione — 600mg vial',
    slug: 'glutathione-600mg',
    category: 'longevity-cellular',
    specification: '600mg vial',
    purity: '99%',
    price: 134.4,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Antioxidant Research'],
    mechanism: 'Glutathione is a tripeptide antioxidant.',
    researchContext: 'Research suggests glutathione may influence oxidative stress and cellular detoxification in preclinical models.',
  },
  {
    id: 'gonadorelin-2mg',
    name: 'Gonadorelin — 2mg vial',
    slug: 'gonadorelin-2mg',
    category: 'hormone-peptide',
    specification: '2mg vial',
    purity: '99%',
    price: 130,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Gonadorelin is a gonadotropin-releasing hormone (GnRH) analog.',
    researchContext: 'Research suggests gonadorelin may influence reproductive hormone pathways in preclinical models.',
  },
  {
    id: 'hgh-10iu',
    name: 'HGH — 10 IU vial',
    slug: 'hgh-10iu',
    category: 'growth-hormone',
    specification: '10 IU vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Human growth hormone (HGH) is a peptide hormone.',
    researchContext: 'Research suggests HGH may influence growth, metabolism, and cellular regeneration in preclinical models.',
  },
  {
    id: 'hgh-12iu',
    name: 'HGH — 12 IU vial',
    slug: 'hgh-12iu',
    category: 'growth-hormone',
    specification: '12 IU vial',
    purity: '99%',
    price: 244.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Human growth hormone (HGH) is a peptide hormone.',
    researchContext: 'Research suggests HGH may influence growth, metabolism, and cellular regeneration in preclinical models.',
  },
  {
    id: 'hgh-15iu',
    name: 'HGH — 15 IU vial',
    slug: 'hgh-15iu',
    category: 'growth-hormone',
    specification: '15 IU vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Human growth hormone (HGH) is a peptide hormone.',
    researchContext: 'Research suggests HGH may influence growth, metabolism, and cellular regeneration in preclinical models.',
  },
  {
    id: 'hgh-24iu',
    name: 'HGH — 24 IU vial',
    slug: 'hgh-24iu',
    category: 'growth-hormone',
    specification: '24 IU vial',
    purity: '99%',
    price: 494.4,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Human growth hormone (HGH) is a peptide hormone.',
    researchContext: 'Research suggests HGH may influence growth, metabolism, and cellular regeneration in preclinical models.',
  },
  {
    id: 'hmg-75iu',
    name: 'HMG — 75 IU vial',
    slug: 'hmg-75iu',
    category: 'hormone-peptide',
    specification: '75 IU vial',
    purity: '99%',
    price: 235.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Human menopausal gonadotropin (HMG) contains FSH and LH.',
    researchContext: 'Research suggests HMG may influence reproductive hormone pathways in preclinical models.',
  },
  {
    id: 'hexarelin-10mg',
    name: 'Hexarelin — 10mg vial',
    slug: 'hexarelin-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 619.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Hexarelin is a growth hormone secretagogue.',
    researchContext: 'Research suggests hexarelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'hexarelin-2mg',
    name: 'Hexarelin — 2mg vial',
    slug: 'hexarelin-2mg',
    category: 'growth-hormone',
    specification: '2mg vial',
    purity: '99%',
    price: 196.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Hexarelin is a growth hormone secretagogue.',
    researchContext: 'Research suggests hexarelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'hexarelin-5mg',
    name: 'Hexarelin — 5mg vial',
    slug: 'hexarelin-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 331.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Hexarelin is a growth hormone secretagogue.',
    researchContext: 'Research suggests hexarelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'igf-1-lr3-0.1mg',
    name: 'IGF-1 LR3 — 0.1mg vial',
    slug: 'igf-1-lr3-0.1mg',
    category: 'growth-hormone',
    specification: '0.1mg vial',
    purity: '99%',
    price: 163.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'IGF-1 LR3 is a modified form of insulin-like growth factor 1.',
    researchContext: 'Research suggests IGF-1 LR3 may influence cellular growth and metabolism in preclinical models.',
  },
  {
    id: 'igf-1-lr3-1mg',
    name: 'IGF-1 LR3 — 1mg vial',
    slug: 'igf-1-lr3-1mg',
    category: 'growth-hormone',
    specification: '1mg vial',
    purity: '99%',
    price: 801.6,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'IGF-1 LR3 is a modified form of insulin-like growth factor 1.',
    researchContext: 'Research suggests IGF-1 LR3 may influence cellular growth and metabolism in preclinical models.',
  },
  {
    id: 'ipamorelin-10mg',
    name: 'Ipamorelin — 10mg vial',
    slug: 'ipamorelin-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 268.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Ipamorelin is a growth hormone secretagogue.',
    researchContext: 'Research suggests ipamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'ipamorelin-5mg',
    name: 'Ipamorelin — 5mg vial',
    slug: 'ipamorelin-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 153.6,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Ipamorelin is a growth hormone secretagogue.',
    researchContext: 'Research suggests ipamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'kpv-10mg',
    name: 'KPV — 10mg vial',
    slug: 'kpv-10mg',
    category: 'recovery-repair',
    specification: '10mg vial',
    purity: '99%',
    price: 268.8,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Anti-Inflammatory Research'],
    mechanism: 'KPV is a tripeptide with potential anti-inflammatory properties.',
    researchContext: 'Research suggests KPV may influence inflammatory pathways in preclinical models.',
  },
  {
    id: 'kisspeptin-10-10mg',
    name: 'Kisspeptin-10 — 10mg vial',
    slug: 'kisspeptin-10-10mg',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 430,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Kisspeptin-10 is a peptide that influences reproductive hormone pathways.',
    researchContext: 'Research suggests kisspeptin-10 may influence GnRH secretion in preclinical models.',
  },
  {
    id: 'kisspeptin-10-5mg',
    name: 'Kisspeptin-10 — 5mg vial',
    slug: 'kisspeptin-10-5mg',
    category: 'hormone-peptide',
    specification: '5mg vial',
    purity: '99%',
    price: 270,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Kisspeptin-10 is a peptide that influences reproductive hormone pathways.',
    researchContext: 'Research suggests kisspeptin-10 may influence GnRH secretion in preclinical models.',
  },
  {
    id: 'l-carnitine-600mg',
    name: 'L-Carnitine — 600mg vial',
    slug: 'l-carnitine-600mg',
    category: 'metabolic',
    specification: '600mg vial',
    purity: '99%',
    price: 153.6,
    image: VIAL_IMAGE,
    researchCategories: ['Metabolic Research'],
    mechanism: 'L-Carnitine is an amino acid derivative involved in fatty acid metabolism.',
    researchContext: 'Research suggests L-carnitine may influence energy metabolism in preclinical models.',
  },
  {
    id: 'mgf-2mg',
    name: 'MGF — 2mg vial',
    slug: 'mgf-2mg',
    category: 'recovery-repair',
    specification: '2mg vial',
    purity: '99%',
    price: 144,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Muscle Research'],
    mechanism: 'Mechano growth factor (MGF) is a splice variant of IGF-1.',
    researchContext: 'Research suggests MGF may influence muscle repair mechanisms in preclinical models.',
  },
  {
    id: 'mots-c-10mg',
    name: 'MOTS-c — 10mg vial',
    slug: 'mots-c-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 270,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Metabolic Research'],
    mechanism: 'MOTS-c is a mitochondrial-derived peptide.',
    researchContext: 'Research suggests MOTS-c may influence metabolic regulation and cellular energy in preclinical models.',
  },
  {
    id: 'mots-c-40mg',
    name: 'MOTS-c — 40mg vial',
    slug: 'mots-c-40mg',
    category: 'longevity-cellular',
    specification: '40mg vial',
    purity: '99%',
    price: 1020,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Metabolic Research'],
    mechanism: 'MOTS-c is a mitochondrial-derived peptide.',
    researchContext: 'Research suggests MOTS-c may influence metabolic regulation and cellular energy in preclinical models.',
  },
  {
    id: 'mt-1',
    name: 'MT-1 — 10mg vial',
    slug: 'mt-1',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Melanotan-1 is a melanocortin receptor agonist.',
    researchContext: 'Research suggests MT-1 may influence melanogenesis in preclinical models.',
  },
  {
    id: 'mt-2',
    name: 'MT-2 — 10mg vial',
    slug: 'mt-2',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'Melanotan-2 is a melanocortin receptor agonist.',
    researchContext: 'Research suggests MT-2 may influence melanogenesis in preclinical models.',
  },
  {
    id: 'melatonin-10mg',
    name: 'Melatonin — 10mg vial',
    slug: 'melatonin-10mg',
    category: 'cognitive-neuro',
    specification: '10mg vial',
    purity: '99%',
    price: 259.2,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Sleep Research'],
    mechanism: 'Melatonin is a hormone that regulates circadian rhythms.',
    researchContext: 'Research suggests melatonin may influence sleep-wake cycles in preclinical models.',
  },
  {
    id: 'nad-100mg',
    name: 'NAD+ — 100mg vial',
    slug: 'nad-100mg',
    category: 'longevity-cellular',
    specification: '100mg vial',
    purity: '99%',
    price: 192,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Metabolic Research'],
    mechanism: 'NAD+ is a coenzyme involved in cellular metabolism.',
    researchContext: 'Research suggests NAD+ may influence cellular energy and aging mechanisms in preclinical models.',
  },
  {
    id: 'nad-1000mg',
    name: 'NAD+ — 1000mg vial',
    slug: 'nad-1000mg',
    category: 'longevity-cellular',
    specification: '1000mg vial',
    purity: '99%',
    price: 744,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Metabolic Research'],
    mechanism: 'NAD+ is a coenzyme involved in cellular metabolism.',
    researchContext: 'Research suggests NAD+ may influence cellular energy and aging mechanisms in preclinical models.',
  },
  {
    id: 'nad-500mg',
    name: 'NAD+ — 500mg vial',
    slug: 'nad-500mg',
    category: 'longevity-cellular',
    specification: '500mg vial',
    purity: '99%',
    price: 384,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Metabolic Research'],
    mechanism: 'NAD+ is a coenzyme involved in cellular metabolism.',
    researchContext: 'Research suggests NAD+ may influence cellular energy and aging mechanisms in preclinical models.',
  },
  {
    id: 'oxytocin-10mg',
    name: 'Oxytocin — 10mg vial',
    slug: 'oxytocin-10mg',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 360,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Neuropeptide Research'],
    mechanism: 'Oxytocin is a peptide hormone and neuropeptide.',
    researchContext: 'Research suggests oxytocin may influence social behavior and bonding in preclinical models.',
  },
  {
    id: 'oxytocin-2mg',
    name: 'Oxytocin — 2mg vial',
    slug: 'oxytocin-2mg',
    category: 'hormone-peptide',
    specification: '2mg vial',
    purity: '99%',
    price: 100.8,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Neuropeptide Research'],
    mechanism: 'Oxytocin is a peptide hormone and neuropeptide.',
    researchContext: 'Research suggests oxytocin may influence social behavior and bonding in preclinical models.',
  },
  {
    id: 'oxytocin-5mg',
    name: 'Oxytocin — 5mg vial',
    slug: 'oxytocin-5mg',
    category: 'hormone-peptide',
    specification: '5mg vial',
    purity: '99%',
    price: 187.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Neuropeptide Research'],
    mechanism: 'Oxytocin is a peptide hormone and neuropeptide.',
    researchContext: 'Research suggests oxytocin may influence social behavior and bonding in preclinical models.',
  },
  {
    id: 'peg-mgf-2mg',
    name: 'PEG-MGF — 2mg vial',
    slug: 'peg-mgf-2mg',
    category: 'recovery-repair',
    specification: '2mg vial',
    purity: '99%',
    price: 331.2,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Muscle Research'],
    mechanism: 'PEG-MGF is a pegylated form of mechano growth factor.',
    researchContext: 'Research suggests PEG-MGF may influence muscle repair mechanisms in preclinical models.',
  },
  {
    id: 'pt-141',
    name: 'PT-141 — 10mg vial',
    slug: 'pt-141',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 259.2,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research'],
    mechanism: 'PT-141 is a melanocortin receptor agonist.',
    researchContext: 'Research suggests PT-141 may influence melanocortin pathways in preclinical models.',
  },
  {
    id: 'ra260-50mg',
    name: 'RA260 — 50mg vial',
    slug: 'ra260-50mg',
    category: 'longevity-cellular',
    specification: '50mg vial',
    purity: '99%',
    price: 268.8,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research'],
    mechanism: 'RA260 is a research compound.',
    researchContext: 'Preclinical research has investigated RA260 in laboratory models.',
  },
  {
    id: 'retatrutide-10mg',
    name: 'Retatrutide — 10mg vial',
    slug: 'retatrutide-10mg',
    category: 'weight-management',
    specification: '10mg vial',
    purity: '99%',
    price: 396,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Retatrutide is a triple agonist peptide.',
    researchContext: 'Research suggests retatrutide may influence metabolic pathways in preclinical models.',
  },
  {
    id: 'retatrutide-20mg',
    name: 'Retatrutide — 20mg vial',
    slug: 'retatrutide-20mg',
    category: 'weight-management',
    specification: '20mg vial',
    purity: '99%',
    price: 492,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Retatrutide is a triple agonist peptide.',
    researchContext: 'Research suggests retatrutide may influence metabolic pathways in preclinical models.',
  },
  {
    id: 'retatrutide-5mg',
    name: 'Retatrutide — 5mg vial',
    slug: 'retatrutide-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 266,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Retatrutide is a triple agonist peptide.',
    researchContext: 'Research suggests retatrutide may influence metabolic pathways in preclinical models.',
  },
  {
    id: 'retatrutide-60mg',
    name: 'Retatrutide — 60mg vial',
    slug: 'retatrutide-60mg',
    category: 'weight-management',
    specification: '60mg vial',
    purity: '99%',
    price: 1282,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Retatrutide is a triple agonist peptide.',
    researchContext: 'Research suggests retatrutide may influence metabolic pathways in preclinical models.',
  },
  {
    id: 'snap-8-10mg',
    name: 'SNAP-8 — 10mg vial',
    slug: 'snap-8-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 163.2,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research'],
    mechanism: 'SNAP-8 is an octapeptide.',
    researchContext: 'Research suggests SNAP-8 may influence neurotransmitter pathways in preclinical models.',
  },
  {
    id: 'ss-31-10mg',
    name: 'SS-31 — 10mg vial',
    slug: 'ss-31-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 405,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Mitochondrial Research'],
    mechanism: 'SS-31 is a mitochondria-targeting peptide.',
    researchContext: 'Research suggests SS-31 may influence mitochondrial function in preclinical models.',
  },
  {
    id: 'ss-31-50mg',
    name: 'SS-31 — 50mg vial',
    slug: 'ss-31-50mg',
    category: 'longevity-cellular',
    specification: '50mg vial',
    purity: '99%',
    price: 1820,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Mitochondrial Research'],
    mechanism: 'SS-31 is a mitochondria-targeting peptide.',
    researchContext: 'Research suggests SS-31 may influence mitochondrial function in preclinical models.',
  },
  {
    id: 'selank-10mg',
    name: 'Selank — 10mg vial',
    slug: 'selank-10mg',
    category: 'cognitive-neuro',
    specification: '10mg vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'Selank is a synthetic peptide with anxiolytic properties.',
    researchContext: 'Research suggests Selank may influence anxiety and cognitive function in preclinical models.',
  },
  {
    id: 'selank-5mg',
    name: 'Selank — 5mg vial',
    slug: 'selank-5mg',
    category: 'cognitive-neuro',
    specification: '5mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'Selank is a synthetic peptide with anxiolytic properties.',
    researchContext: 'Research suggests Selank may influence anxiety and cognitive function in preclinical models.',
  },
  {
    id: 'semaglutide-10mg',
    name: 'Semaglutide — 10mg vial',
    slug: 'semaglutide-10mg',
    category: 'weight-management',
    specification: '10mg vial',
    purity: '99%',
    price: 226,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Semaglutide is a GLP-1 receptor agonist.',
    researchContext: 'Research suggests semaglutide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'semaglutide-20mg',
    name: 'Semaglutide — 20mg vial',
    slug: 'semaglutide-20mg',
    category: 'weight-management',
    specification: '20mg vial',
    purity: '99%',
    price: 316,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Semaglutide is a GLP-1 receptor agonist.',
    researchContext: 'Research suggests semaglutide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'semaglutide-5mg',
    name: 'Semaglutide — 5mg vial',
    slug: 'semaglutide-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 144,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Semaglutide is a GLP-1 receptor agonist.',
    researchContext: 'Research suggests semaglutide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'semax-10mg',
    name: 'Semax — 10mg vial',
    slug: 'semax-10mg',
    category: 'cognitive-neuro',
    specification: '10mg vial',
    purity: '99%',
    price: 307.2,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'Semax is a synthetic peptide with nootropic properties.',
    researchContext: 'Research suggests Semax may influence cognitive function and neuroprotection in preclinical models.',
  },
  {
    id: 'semax-5mg',
    name: 'Semax — 5mg vial',
    slug: 'semax-5mg',
    category: 'cognitive-neuro',
    specification: '5mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Cognitive Research', 'Neuropeptide Research'],
    mechanism: 'Semax is a synthetic peptide with nootropic properties.',
    researchContext: 'Research suggests Semax may influence cognitive function and neuroprotection in preclinical models.',
  },
  {
    id: 'sermorelin-10mg',
    name: 'Sermorelin — 10mg vial',
    slug: 'sermorelin-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 547.2,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Sermorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests sermorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'sermorelin-5mg',
    name: 'Sermorelin — 5mg vial',
    slug: 'sermorelin-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 288,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Sermorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests sermorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'tb-500-10mg',
    name: 'TB-500 — 10mg vial',
    slug: 'tb-500-10mg',
    category: 'recovery-repair',
    specification: '10mg vial',
    purity: '99%',
    price: 667.2,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'TB-500 is a synthetic peptide derived from thymosin beta-4.',
    researchContext: 'Research suggests TB-500 may influence tissue repair and regeneration mechanisms in preclinical models.',
  },
  {
    id: 'tb-500-2mg',
    name: 'TB-500 — 2mg vial',
    slug: 'tb-500-2mg',
    category: 'recovery-repair',
    specification: '2mg vial',
    purity: '99%',
    price: 163.2,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'TB-500 is a synthetic peptide derived from thymosin beta-4.',
    researchContext: 'Research suggests TB-500 may influence tissue repair and regeneration mechanisms in preclinical models.',
  },
  {
    id: 'tb-500-5mg',
    name: 'TB-500 — 5mg vial',
    slug: 'tb-500-5mg',
    category: 'recovery-repair',
    specification: '5mg vial',
    purity: '99%',
    price: 340.8,
    image: VIAL_IMAGE,
    researchCategories: ['Recovery & Repair Research', 'Musculoskeletal Research'],
    mechanism: 'TB-500 is a synthetic peptide derived from thymosin beta-4.',
    researchContext: 'Research suggests TB-500 may influence tissue repair and regeneration mechanisms in preclinical models.',
  },
  {
    id: 'tesamorelin-10mg',
    name: 'Tesamorelin — 10mg vial',
    slug: 'tesamorelin-10mg',
    category: 'growth-hormone',
    specification: '10mg vial',
    purity: '99%',
    price: 801.6,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Tesamorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests tesamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'tesamorelin-2mg',
    name: 'Tesamorelin — 2mg vial',
    slug: 'tesamorelin-2mg',
    category: 'growth-hormone',
    specification: '2mg vial',
    purity: '99%',
    price: 206.4,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Tesamorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests tesamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'tesamorelin-20mg',
    name: 'Tesamorelin — 20mg vial',
    slug: 'tesamorelin-20mg',
    category: 'growth-hormone',
    specification: '20mg vial',
    purity: '99%',
    price: 1540.8,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Tesamorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests tesamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'tesamorelin-5mg',
    name: 'Tesamorelin — 5mg vial',
    slug: 'tesamorelin-5mg',
    category: 'growth-hormone',
    specification: '5mg vial',
    purity: '99%',
    price: 422.4,
    image: VIAL_IMAGE,
    researchCategories: ['Growth Hormone Research', 'Hormone & Peptide Research'],
    mechanism: 'Tesamorelin is a growth hormone-releasing hormone (GHRH) analog.',
    researchContext: 'Research suggests tesamorelin may influence growth hormone secretion in preclinical models.',
  },
  {
    id: 'thymalin-10mg',
    name: 'Thymalin — 10mg vial',
    slug: 'thymalin-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 280,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Immune Research'],
    mechanism: 'Thymalin is a thymus-derived peptide complex.',
    researchContext: 'Research suggests thymalin may influence immune function in preclinical models.',
  },
  {
    id: 'thymosin-alpha1-10mg',
    name: 'Thymosin-α1 — 10mg vial',
    slug: 'thymosin-alpha1-10mg',
    category: 'longevity-cellular',
    specification: '10mg vial',
    purity: '99%',
    price: 695,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Immune Research'],
    mechanism: 'Thymosin-α1 is a thymus-derived peptide.',
    researchContext: 'Research suggests thymosin-α1 may influence immune function in preclinical models.',
  },
  {
    id: 'thymosin-alpha1-5mg',
    name: 'Thymosin-α1 — 5mg vial',
    slug: 'thymosin-alpha1-5mg',
    category: 'longevity-cellular',
    specification: '5mg vial',
    purity: '99%',
    price: 385,
    image: VIAL_IMAGE,
    researchCategories: ['Longevity & Cellular Research', 'Immune Research'],
    mechanism: 'Thymosin-α1 is a thymus-derived peptide.',
    researchContext: 'Research suggests thymosin-α1 may influence immune function in preclinical models.',
  },
  {
    id: 'tirzepatide-100mg',
    name: 'Tirzepatide — 100mg vial',
    slug: 'tirzepatide-100mg',
    category: 'weight-management',
    specification: '100mg vial',
    purity: '99%',
    price: 1080,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Tirzepatide is a dual GIP/GLP-1 receptor agonist.',
    researchContext: 'Research suggests tirzepatide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'tirzepatide-20mg',
    name: 'Tirzepatide — 20mg vial',
    slug: 'tirzepatide-20mg',
    category: 'weight-management',
    specification: '20mg vial',
    purity: '99%',
    price: 340,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Tirzepatide is a dual GIP/GLP-1 receptor agonist.',
    researchContext: 'Research suggests tirzepatide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'tirzepatide-5mg',
    name: 'Tirzepatide — 5mg vial',
    slug: 'tirzepatide-5mg',
    category: 'weight-management',
    specification: '5mg vial',
    purity: '99%',
    price: 154,
    image: VIAL_IMAGE,
    researchCategories: ['Weight Management Research', 'Metabolic Research'],
    mechanism: 'Tirzepatide is a dual GIP/GLP-1 receptor agonist.',
    researchContext: 'Research suggests tirzepatide may influence glucose metabolism and appetite regulation in preclinical models.',
  },
  {
    id: 'vip-10mg',
    name: 'VIP — 10mg vial',
    slug: 'vip-10mg',
    category: 'hormone-peptide',
    specification: '10mg vial',
    purity: '99%',
    price: 720,
    image: VIAL_IMAGE,
    researchCategories: ['Hormone & Peptide Research', 'Neuropeptide Research'],
    mechanism: 'Vasoactive intestinal peptide (VIP) is a neuropeptide.',
    researchContext: 'Research suggests VIP may influence various physiological processes in preclinical models.',
  },
];

// Featured products for homepage showcase
export const featuredProducts: Product[] = [
  allProducts.find(p => p.slug === 'bpc-157-10mg')!,
  allProducts.find(p => p.slug === 'cjc-ipamorelin-10mg')!,
  allProducts.find(p => p.slug === 'semaglutide-5mg')!,
  allProducts.find(p => p.slug === 'tb-500-10mg')!,
  allProducts.find(p => p.slug === 'ipamorelin-10mg')!,
  allProducts.find(p => p.slug === 'ghk-cu-50mg')!,
  allProducts.find(p => p.slug === 'mots-c-10mg')!,
  allProducts.find(p => p.slug === 'epithalon-10mg')!,
].filter(Boolean);
